import type { ClientConfig, Reward } from "../types/client";

const PROBABILITY_TOLERANCE = 0.000001;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`Configuration field “${key}” must be a non-empty string.`);
  }
  return value;
}

function validateReward(value: unknown, index: number): Reward {
  if (!isRecord(value)) {
    throw new Error(`Reward ${index + 1} must be an object.`);
  }

  const probability = value.probability;
  if (typeof probability !== "number" || !Number.isFinite(probability) || probability < 0 || probability > 1) {
    throw new Error(`Reward ${index + 1} has an invalid probability.`);
  }
  if (typeof value.enabled !== "boolean") {
    throw new Error(`Reward ${index + 1} must define enabled as true or false.`);
  }

  return {
    id: requireString(value, "id"),
    name: requireString(value, "name"),
    displayName: requireString(value, "displayName"),
    probability,
    couponCode: requireString(value, "couponCode"),
    enabled: value.enabled,
  };
}

export function validateClientConfig(value: unknown): ClientConfig {
  if (!isRecord(value)) {
    throw new Error("Client configuration must be an object.");
  }
  if (!isRecord(value.logo)) {
    throw new Error("Configuration field “logo” must be an object.");
  }
  if (!Array.isArray(value.rewards) || value.rewards.length === 0) {
    throw new Error("At least one reward is required.");
  }

  const rewards = value.rewards.map(validateReward);
  const enabledRewards = rewards.filter((reward) => reward.enabled);
  if (enabledRewards.length < 2) {
    throw new Error("At least two rewards must be enabled to render a wheel.");
  }

  const duplicateIds = rewards.filter(
    (reward, index) => rewards.findIndex((candidate) => candidate.id === reward.id) !== index,
  );
  if (duplicateIds.length > 0) {
    throw new Error(`Reward IDs must be unique. Duplicate: ${duplicateIds[0].id}`);
  }

  const probabilityTotal = enabledRewards.reduce((total, reward) => total + reward.probability, 0);
  if (Math.abs(probabilityTotal - 1) > PROBABILITY_TOLERANCE) {
    throw new Error(
      `Enabled reward probabilities must total 1. Current total: ${probabilityTotal.toFixed(6)}.`,
    );
  }

  return {
    slug: requireString(value, "slug"),
    businessName: requireString(value, "businessName"),
    logo: {
      mark: requireString(value.logo, "mark"),
      wordmark: requireString(value.logo, "wordmark"),
      imageUrl: value.logo.imageUrl === null ? null : requireString(value.logo, "imageUrl"),
    },
    primaryColor: requireString(value, "primaryColor"),
    secondaryColor: requireString(value, "secondaryColor"),
    backgroundColor: requireString(value, "backgroundColor"),
    surfaceColor: requireString(value, "surfaceColor"),
    textColor: requireString(value, "textColor"),
    headline: requireString(value, "headline"),
    description: requireString(value, "description"),
    ctaText: requireString(value, "ctaText"),
    claimText: requireString(value, "claimText"),
    rewards,
  };
}
